import { useMemo } from 'react';

import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FertilizationSiteDropdown } from '@/components';
import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { AdvancedDonorLookup } from '@/components/common/advanced-donor-lookup';
import { AdvancedSemenLookup } from '@/components/common/advanced-semen-lookup';
import { ApprovalNumberAutoComplete } from '@/components/common/approval-number-auto-complete';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { CellLineAutoComplete } from '@/components/common/cell-line-auto-complete';
import { StorageLocationAutoComplete } from '@/components/common/storage-location-auto-complete';
import {
  StorageLocationCombobox,
  type StorageLocationOption,
} from '@/components/common/storage-location-combobox';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FormField } from '@/pages/embryo-details/components/FormField';
import { useMergeValueIntoOptions } from '@/pages/embryo-details/hooks/useMergeValueIntoOptions';
import { EmbryoDetailDto } from '@/services/embryo-service';

import {
  OUTSIDE_EMBRYO_ADDITIONAL_FIELDS,
  OUTSIDE_EMBRYO_EDITABLE_FIELDS,
} from './constants';
import { EmbryoFormFieldConfig } from './field-config';
import {
  accountTypeOptions,
  freshFrozenOptions,
  stateCodeOptions,
  statusReasonOptions,
  useAccountOptions,
} from './form-options';
import { StatusCommentSection } from './StatusCommentSection';

const fieldsetPt = {
  root: { className: 'p-2' },
  content: { className: 'space-y-2' },
};

type SectionConfig = {
  title: string;
  auditOnly?: boolean;
  fields: EmbryoFormFieldConfig[];
};

type EmbryoAdditionalTabColumns = 3 | 4 | 5;

type EmbryoAdditionalTabProps = {
  control: Control<EmbryoDetailDto>;
  watch: UseFormWatch<EmbryoDetailDto>;
  setValue: UseFormSetValue<EmbryoDetailDto>;
  isBulkMode: boolean;
  hasManagerWrite: boolean;
  columns?: EmbryoAdditionalTabColumns;
  originalStateCode?: number;
  isOutsideEmbryo?: boolean;
};

export function EmbryoAdditionalTab({
  control,
  watch,
  setValue,
  isBulkMode,
  hasManagerWrite,
  columns = 3,
  originalStateCode,
  isOutsideEmbryo = false,
}: EmbryoAdditionalTabProps) {
  const { t } = useTranslation();
  const addRemoveEmbryoFields = useFeatureFlag('Add_Remove_Embryo_Fields');
  const isStorageLocationPagingEnabled = useFeatureFlag(
    'Storage_Location_Paging',
  );
  const accountOptions = useAccountOptions();

  const watchAny: UseFormWatch<EmbryoDetailDto> = watch;
  const oldStorageLocationId = watch('oldStorageLocationId');
  const oldStorageLocationName = watch('oldStorageLocationName');
  const ownerOptions = useMergeValueIntoOptions({
    currentOption: {
      label: watchAny('oldOwnerName') ?? '',
      value: watchAny('oldOwnerId') ?? '',
    },
    options: accountOptions,
  });

  const soldToValue = watchAny('soldTo');
  const hasSoldTo = !!soldToValue && !!soldToValue.trim();

  const removeStatusActivityRestrictions = useFeatureFlag(
    'Remove_Status_Activity_Restrictions',
  );
  const isEmbryoRecipOwnerFieldEnabled = useFeatureFlag(
    'Embryo_Recip_Owner_Field',
  );
  const bulkLockedFieldsEnabled = useFeatureFlag(
    'Embryo_Bulk_Edit_Locked_Fields',
  );

  const MATING_LOCKED_FIELDS = (() => {
    const base = addRemoveEmbryoFields
      ? ['name', 'stateCode']
      : ['dateUsed', 'name', 'accountType', 'stateCode', 'statusCode'];
    return removeStatusActivityRestrictions
      ? base.filter((f) => f !== 'stateCode' && f !== 'statusCode')
      : base;
  })();

  const lockedFields = addRemoveEmbryoFields
    ? ['createdByName', 'creation', 'modifiedByName', 'modifiedOn', 'sireName']
    : [
        'createdByName',
        'creation',
        'modifiedByName',
        'modifiedOn',
        'recId',
        'embryoOutsideEntryId',
        'recipOwnerId',
        'oldOwnerId',
        'oldStorageLocationId',
        'originalFreezeSiteId',
        'sireName',
      ];

  // Recip Owner is only unlocked for single-record edits; bulk keeps it
  // locked regardless of the flag (WI 37965).
  const disabledFields =
    isEmbryoRecipOwnerFieldEnabled &&
    !(isBulkMode && bulkLockedFieldsEnabled && !isOutsideEmbryo)
      ? lockedFields.filter((field) => field !== 'recipOwnerId')
      : lockedFields;

  const sections: SectionConfig[] = useMemo(
    () => [
      {
        title: t('common.auditInformation'),
        auditOnly: true,
        fields: [
          {
            name: 'createdByName',
            label: t('common.createdBy'),
            component: InputText,
            ...(addRemoveEmbryoFields && { required: true }),
          },
          {
            name: 'creation',
            label: t('common.createdOn'),
            component: Calendar,
            ...(addRemoveEmbryoFields && { required: true }),
            componentProps: { dateFormat: 'mm/dd/yy' },
          },
          {
            name: 'modifiedByName',
            label: t('common.modifiedBy'),
            component: InputText,
          },
          {
            name: 'modifiedOn',
            label: t('common.modifiedOn'),
            component: Calendar,
            componentProps: { dateFormat: 'mm/dd/yy' },
          },
        ],
      },
      {
        title: t('common.billingAccounting'),
        fields: [
          ...(addRemoveEmbryoFields
            ? []
            : [
                {
                  name: 'accountType',
                  label: t('embryo.accountType'),
                  component: Dropdown,
                  componentProps: {
                    placeholder: 'Account Type',
                    options: accountTypeOptions,
                  },
                },
              ]),
          {
            name: 'recoveryFirmCode',
            label: t('freeze.recoveryFirmCode'),
            component: InputText,
          },
          {
            name: 'recoveryFirmName',
            label: t('embryo.recoveryFirmName'),
            component: InputText,
          },
        ],
      },
      {
        title: t('embryo.donorBreeding'),
        fields: [
          {
            name: 'donorAge',
            label: t('embryo.donorAge'),
            component: InputNumber,
          },
          {
            name: 'semenId',
            label: t('semen.semen'),
            component: AdvancedSemenLookup,
          },
          { name: 'sireName', label: t('embryo.sire'), component: InputText },
          {
            name: 'probableDonorId',
            label: t('embryo.probableDonorFromPool'),
            component: AdvancedDonorLookup,
          },
        ],
      },
      {
        title: t('embryo.locationOwnership'),
        fields: [
          {
            name: 'oldStorageLocationId',
            label: t('embryo.originalLocation'),
            component: isStorageLocationPagingEnabled
              ? StorageLocationCombobox
              : StorageLocationAutoComplete,
            componentProps: isStorageLocationPagingEnabled
              ? {
                  value:
                    oldStorageLocationId && oldStorageLocationName
                      ? {
                          id: oldStorageLocationId,
                          name: oldStorageLocationName,
                        }
                      : null,
                  onChange: (location: StorageLocationOption | null) => {
                    setValue('oldStorageLocationId', location?.id, {
                      shouldDirty: true,
                    });
                    setValue(
                      'oldStorageLocationName',
                      location?.name ?? undefined,
                    );
                  },
                  pagingEnabled: true,
                  placeholder: t('common.selectOption'),
                }
              : {
                  field: 'name',
                  placeholder: t('common.selectOption'),
                  defaultValue:
                    watchAny('oldStorageLocationId') &&
                    watchAny('oldStorageLocationName')
                      ? {
                          id: watchAny('oldStorageLocationId'),
                          name: watchAny('oldStorageLocationName'),
                        }
                      : null,
                },
          },
          {
            name: 'oldOwnerId',
            label: t('embryo.originalOwner'),
            component: Dropdown,
            componentProps: {
              options: ownerOptions,
              placeholder: t('common.selectOption'),
              loading: accountOptions.length === 0,
            },
          },
          {
            name: 'originalFreezeSiteId',
            label: t('embryo.originalFreezeSite'),
            component: FertilizationSiteDropdown,
            componentProps: {
              placeholder: t('common.selectOption'),
            },
          },
          { name: 'origin', label: t('embryo.origin'), component: InputText },
          {
            name: 'recipOwnerId',
            label: t('embryo.recipOwner'),
            component: addRemoveEmbryoFields ? AccountsCombobox : Dropdown,
            componentProps: addRemoveEmbryoFields
              ? {}
              : {
                  options: ownerOptions,
                  placeholder: t('common.selectOption'),
                  loading: accountOptions.length === 0,
                },
          },
        ],
      },
      {
        title: t('embryo.identification'),
        fields: [
          ...(!addRemoveEmbryoFields
            ? [
                {
                  name: 'recId',
                  label: t('embryo.recordId'),
                  component: InputText,
                },
              ]
            : []),
          {
            name: 'togId',
            label: t('embryo.togId'),
            component: InputText,
          },
          {
            name: 'name',
            label: t('embryo.embryoName'),
            component: InputText,
            ...(addRemoveEmbryoFields && { required: true }),
          },
          ...(!addRemoveEmbryoFields
            ? [
                {
                  name: 'embTag',
                  label: t('embryo.embryoTagText'),
                  component: InputText,
                },
              ]
            : []),
          {
            name: 'embryoOutsideEntryId',
            label: t('embryo.outsideEntryId'),
            component: InputText,
          },
          ...(!addRemoveEmbryoFields
            ? [
                {
                  name: 'group',
                  label: t('embryo.group'),
                  component: InputNumber,
                },
              ]
            : []),
          {
            name: 'cellId',
            label: t('embryo.cellId'),
            component: InputText,
          },
          {
            name: 'cellLineId',
            label: t('embryo.cellLineName'),
            component: CellLineAutoComplete,
          },
          {
            name: 'dairyCompEmbryoId',
            label: t('embryo.dairyCompId'),
            component: InputNumber,
          },
          ...(!addRemoveEmbryoFields
            ? [
                {
                  name: 'embryoStagingId',
                  label: t('embryo.embryoStagingId'),
                  component: InputNumber,
                },
              ]
            : []),
          {
            name: 'approvalNumberId',
            label: t('embryo.approvalNumber'),
            component: ApprovalNumberAutoComplete,
          },
          {
            name: 'tempEmbryoId',
            label: t('embryo.tempEmbryoId'),
            component: InputText,
          },
        ],
      },
      {
        title: t('embryo.productionInformation'),
        fields: [
          {
            name: 'siteId',
            label: t('embryo.fertilizationId'),
            component: FertilizationSiteDropdown,
            componentProps: {
              placeholder: t('site.selectSite'),
            },
          },
          {
            name: 'flushName',
            label: t('embryo.flush'),
            component: InputText,
          },
          {
            name: 'flushIdNumber',
            label: t('embryo.flshidn'),
            component: InputNumber,
          },
          {
            name: 'freshFrozen',
            label: t('embryo.freshFrozen'),
            component: Dropdown,
            ...(addRemoveEmbryoFields && { required: true }),
            componentProps: {
              options: freshFrozenOptions,
              placeholder: t('common.selectType'),
            },
          },
          ...(!addRemoveEmbryoFields
            ? [
                {
                  name: 'dateUsed',
                  label: t('embryo.dateUsed'),
                  component: Calendar,
                  componentProps: { dateFormat: 'mm/dd/yy' },
                },
              ]
            : []),
          {
            name: 'estDueDate',
            label: t('embryo.estDueDate'),
            component: Calendar,
            componentProps: { dateFormat: 'mm/dd/yy' },
          },
          ...(addRemoveEmbryoFields
            ? [
                {
                  name: 'estrusSync',
                  label: t('embryo.estrusSync'),
                  component: InputNumber,
                },
              ]
            : []),
        ],
      },
      {
        title: t('embryo.qualityGrading'),
        fields: [
          {
            name: 'frozenStage',
            label: t('freeze.frozenStage'),
            component: InputNumber,
          },
          {
            name: 'frozenGrade',
            label: t('embryo.frozenGrade'),
            component: InputNumber,
          },
          {
            name: 'grader7Name',
            label: t('grading.grader'),
            component: InputText,
          },
          {
            name: 'thawByName',
            label: t('freeze.thawBy'),
            component: InputText,
          },
          {
            name: 'quantity',
            label: t('common.quantity'),
            component: InputNumber,
          },
          {
            name: 'strawNumber',
            label: t('embryo.strawText'),
            component: InputNumber,
          },
          ...(addRemoveEmbryoFields
            ? [
                {
                  name: 'psi',
                  label: t('embryo.paPsi'),
                  component: InputNumber,
                },
              ]
            : []),
        ],
      },
      {
        title: t('embryo.transactionInformation'),
        fields: [
          {
            name: 'soldTo',
            label: t('embryo.soldTo'),
            component: InputText,
          },
          ...(!addRemoveEmbryoFields
            ? [
                {
                  name: 'transferDate',
                  label: t('embryo.transDate'),
                  component: Calendar,
                  componentProps: { dateFormat: 'mm/dd/yy' },
                  required: hasSoldTo,
                },
                {
                  name: 'transComment',
                  label: t('embryo.transComment'),
                  component: InputTextarea,
                  componentProps: { rows: 3 },
                  required: hasSoldTo,
                },
              ]
            : []),
        ],
      },
      {
        title: t('embryo.statusActivity'),
        fields: [
          {
            name: 'stateCode',
            label: t('embryo.state'),
            component: Dropdown,
            componentProps: {
              options: stateCodeOptions,
              placeholder: t('common.selectOption'),
            },
          },
          ...(!addRemoveEmbryoFields
            ? [
                {
                  name: 'statusCode',
                  label: t('embryo.statusReason'),
                  component: Dropdown,
                  componentProps: {
                    options: statusReasonOptions,
                    placeholder: 'Select status',
                  },
                },
                {
                  name: 'lastAct',
                  label: t('common.lastActivity'),
                  component: InputText,
                },
              ]
            : []),
        ],
      },
    ],
    [
      t,
      accountOptions.length,
      addRemoveEmbryoFields,
      hasSoldTo,
      isStorageLocationPagingEnabled,
      oldStorageLocationId,
      oldStorageLocationName,
      ownerOptions,
      setValue,
      watchAny,
    ],
  );

  if (isOutsideEmbryo) {
    const allFields = sections.flatMap((s) => s.fields);
    const visibleFields = allFields.filter((f) =>
      (OUTSIDE_EMBRYO_ADDITIONAL_FIELDS as readonly string[]).includes(f.name),
    );

    return (
      <div className="additional-tog-frozen-embryo-tab">
        <Fieldset
          pt={{
            root: { className: 'p-2' },
            content: { className: 'space-y-2' },
          }}
        >
          <div
            className="grid gap-x-6 gap-y-1"
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            }}
          >
            {visibleFields.map((field) => {
              const lockedByOutsideEntry = !(
                OUTSIDE_EMBRYO_EDITABLE_FIELDS as readonly string[]
              ).includes(field.name);
              return (
                <FormField
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  control={control}
                  component={field.component}
                  disabled={
                    field.disabled ??
                    (lockedByOutsideEntry ||
                      disabledFields.includes(field.name) ||
                      (MATING_LOCKED_FIELDS.includes(field.name) &&
                        !hasManagerWrite))
                  }
                  required={field.required}
                  componentProps={field.componentProps}
                />
              );
            })}
          </div>
        </Fieldset>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div
        className="grid gap-x-4 gap-y-4"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {sections.map((section) => {
          if (section.auditOnly && isBulkMode) return null;
          return (
            <Fieldset key={section.title} pt={fieldsetPt}>
              <h3 className="mb-2 text-lg font-semibold">{section.title}</h3>
              <div className="flex flex-col gap-y-1">
                {section.fields.map((field) => (
                  <FormField
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    control={control}
                    component={field.component}
                    disabled={
                      field.disabled ??
                      (disabledFields.includes(field.name) ||
                        (MATING_LOCKED_FIELDS.includes(field.name) &&
                          !hasManagerWrite))
                    }
                    required={field.required}
                    componentProps={field.componentProps}
                  />
                ))}
                {section.title === t('embryo.statusActivity') && (
                  <StatusCommentSection
                    control={control}
                    statusComment={watchAny('statusComment') || ''}
                    setValue={setValue}
                    disabled={
                      removeStatusActivityRestrictions
                        ? false
                        : !hasManagerWrite
                    }
                    required={
                      originalStateCode !== undefined &&
                      watchAny('stateCode') !== undefined &&
                      watchAny('stateCode') !== originalStateCode
                    }
                  />
                )}
              </div>
            </Fieldset>
          );
        })}
      </div>
    </div>
  );
}
